import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, ExternalLink, Globe, Image, Info, Radio, Upload, X } from 'lucide-react';
import { api } from '../../lib/api';
import { DEFAULT_BRAND, useBranding, type Branding } from '../../lib/branding';
import {
  applyBody,
  domainPollInterval,
  formOffersSmartolt,
  hostRows,
  type DomainStatusView,
  type HostRow,
  type RowTone,
} from '../../lib/domain-hosts';
import { useSession } from '../../lib/session';
import { Button, Card, ErrorNotice, Field, Input, Pill, Spinner } from '../../components/ui';

/** Anything larger is refused by the API, so it is refused here first. */
const LOGO_MAX_BYTES = 256 * 1024;
const LOGO_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];

const BLANK: Branding = {
  displayName: null,
  tagline: null,
  logoDataUri: null,
  accentColor: null,
  supportEmail: null,
  supportUrl: null,
  footerNote: null,
};

/**
 * White labeling: whose product this looks like.
 *
 * Two scopes, because they are seen by different people at different moments.
 * An organization's branding starts the instant somebody signs in, and is what
 * that operator's own staff see all day. The platform's branding is what
 * everybody sees before signing in, which is why only a superadmin sets it —
 * the login page belongs to the installation, not to any one tenant.
 *
 * Fields fall back rather than replace: an org that uploads only a logo keeps
 * the platform's name and colour, and clearing a field returns it to whatever
 * sits behind it rather than blanking the interface.
 */
export function WhiteLabelTab() {
  const { session, activeOrg } = useSession();
  const query = useBranding();
  const isSuperadmin = session?.user.role === 'superadmin';

  if (query.isLoading) {
    return (
      <Card>
        <div className="flex justify-center p-10">
          <Spinner />
        </div>
      </Card>
    );
  }
  if (query.error) {
    return (
      <Card>
        <div className="p-5">
          <ErrorNotice error={query.error} />
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-5">
      <BrandForm
        key={`org-${query.data?.orgId ?? 'none'}`}
        title={activeOrg ? `${activeOrg.name} branding` : 'Organization branding'}
        hint="Shown to this organization's people from the moment they sign in. Anything left blank falls back to the platform's."
        value={query.data?.org ?? BLANK}
        fallback={query.data?.platform ?? BLANK}
        endpoint="/branding"
        disabled={!query.data?.orgId}
        disabledReason="Select an organization in the top bar to brand it."
      />

      {isSuperadmin && (
        <BrandForm
          key="platform"
          title="Platform branding"
          hint="The login page and the default behind every organization. Everybody sees this, including people not yet signed in — which is why it is superadmin-only."
          value={query.data?.platform ?? BLANK}
          fallback={BLANK}
          endpoint="/branding/platform"
        />
      )}

      <DomainCard hasOrg={Boolean(activeOrg) || isSuperadmin} />

      <StatusEmbedCard hasOrg={Boolean(activeOrg)} />

      {isSuperadmin && <PlatformSubdomainsCard />}
    </div>
  );
}

interface StatusEmbedView {
  slug: string;
  showChassisNames: boolean;
  jsonPath: string;
  embedPath: string;
}

/**
 * The org's PUBLIC status embed: the <iframe> snippet for their own website,
 * the JSON URL for anything programmatic, and the one privacy decision on the
 * surface — whether the widget may print real chassis names. Off (the
 * default) it says "PON network 1", "PON network 2"…, because the page is
 * unauthenticated and a chassis name on it names a piece of the network to
 * the whole internet.
 */
function StatusEmbedCard({ hasOrg }: { hasOrg: boolean }) {
  const queryClient = useQueryClient();
  const [copied, setCopied] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['branding', 'status-embed'],
    queryFn: () => api.get<StatusEmbedView>('/branding/status-embed'),
    enabled: hasOrg,
  });

  const save = useMutation({
    mutationFn: (showChassisNames: boolean) =>
      api.put<{ showChassisNames: boolean }>('/branding/status-embed', { showChassisNames }),
    onSuccess: async () => {
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['branding', 'status-embed'] });
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Could not save'),
  });

  const copy = async (what: string, text: string) => {
    await navigator.clipboard.writeText(text);
    setCopied(what);
    setTimeout(() => setCopied(null), 2500);
  };

  const d = query.data;
  const embedUrl = d ? `${window.location.origin}${d.embedPath}` : '';
  const jsonUrl = d ? `${window.location.origin}${d.jsonPath}` : '';
  /*
    The embed tells its parent its height (`simpleacs-status-height`), and the
    snippet's few lines listen for it, so the page is never cut off or padded
    whatever the history and incidents hold. Without the script the iframe is
    simply its starting height.
  */
  const snippet = d
    ? [
        `<iframe id="network-status" src="${embedUrl}" title="Network status" width="100%" height="1400" style="border:0;max-width:960px" loading="lazy"></iframe>`,
        `<script>addEventListener('message',function(e){var f=document.getElementById('network-status');if(f&&e.source===f.contentWindow&&e.data&&e.data.type==='simpleacs-status-height'){f.style.height=e.data.height+'px'}})</script>`,
      ].join('\n')
    : '';

  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <Radio className="size-4 text-muted" />
          Public status embed
        </span>
      }
      action={
        d ? (
          <a
            href={embedUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 text-xs text-link hover:underline"
          >
            Open it <ExternalLink className="size-3.5" />
          </a>
        ) : undefined
      }
    >
      {!hasOrg ? (
        <p className="p-5 text-sm text-muted">Select an organization in the top bar first.</p>
      ) : query.isLoading ? (
        <div className="flex justify-center p-8">
          <Spinner />
        </div>
      ) : query.error || !d ? (
        <div className="p-5">
          <ErrorNotice error={query.error ?? new Error('Could not load the embed settings')} />
        </div>
      ) : (
        <div className="space-y-4 p-5">
          <p className="text-sm text-muted">
            A status page for this organization's own website, wearing its white-label settings —
            logo, name in the accent colour, tagline, support link and email, and footer note. It
            opens on one plain sentence ("… is up and running"), then each service's state, a
            90-day uptime history, response-time and online-share charts by day, week or month,
            and the last week's incidents, all in East Africa Time and refreshed every minute. No sign-in, and nothing sensitive: percentages, never
            counts, and no addresses. Paste the snippet where the status should appear (it sizes
            itself), add <span className="font-mono text-xs">?theme=light</span> or{' '}
            <span className="font-mono text-xs">?theme=dark</span> to the URL to fix its look, or
            read the JSON from anything programmatic.
          </p>

          <div className="space-y-1.5">
            <p className="text-xs font-medium text-ink">Preview</p>
            <iframe
              src={embedUrl}
              title="Public status preview"
              className="h-[640px] w-full max-w-4xl rounded-lg border border-line bg-surface-2"
              loading="lazy"
            />
          </div>

          <div className="space-y-1.5">
            <p className="text-xs font-medium text-ink">The snippet</p>
            <pre className="overflow-x-auto rounded-lg border border-line bg-surface-2 p-3 font-mono text-xs text-ink">{snippet}</pre>
          </div>
          <div className="space-y-1.5">
            <p className="text-xs font-medium text-ink">The JSON</p>
            <pre className="overflow-x-auto rounded-lg border border-line bg-surface-2 p-3 font-mono text-xs text-ink">{jsonUrl}</pre>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="secondary" onClick={() => copy('snippet', snippet)}>
              {copied === 'snippet' ? 'Copied' : 'Copy the snippet'}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => copy('json', jsonUrl)}>
              {copied === 'json' ? 'Copied' : 'Copy the JSON URL'}
            </Button>
          </div>

          <label className="flex cursor-pointer items-start gap-2 text-sm text-ink">
            <input
              type="checkbox"
              className="mt-0.5 size-4 accent-primary"
              checked={d.showChassisNames}
              disabled={save.isPending}
              onChange={(e) => save.mutate(e.target.checked)}
            />
            <span>
              Show real chassis names on the embed
              <span className="block text-xs text-muted">
                Off, each chassis appears as "PON network 1", "PON network 2"… — the page is
                public, so naming equipment on it is this organization's call, not the default.
              </span>
            </span>
          </label>

          {error && <ErrorNotice error={new Error(error)} />}
        </div>
      )}
    </Card>
  );
}

interface PlatformDomainView {
  baseDomain: string;
  platformHost: string;
  example: string | null;
  tlsNote: string;
}

/**
 * The per-org subdomain scheme: one base domain, every organization at
 * <slug>.<base> automatically — no per-org DNS, no per-org vhost. Setting it
 * writes the nginx wildcard through the same root helper as white-label
 * hosts; the operator's two remaining steps (wildcard DNS + wildcard TLS)
 * are spelled out rather than assumed.
 */
function PlatformSubdomainsCard() {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['branding', 'platform-domain'],
    queryFn: () => api.get<PlatformDomainView>('/branding/platform-domain'),
  });
  const [value, setValue] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: (baseDomain: string) => api.put('/branding/platform-domain', { baseDomain }),
    onSuccess: async (_r, baseDomain) => {
      setError(null);
      setNotice(baseDomain ? `Subdomains live under ${baseDomain}.` : 'Subdomain scheme switched off.');
      setValue(null);
      setTimeout(() => setNotice(null), 6000);
      await queryClient.invalidateQueries({ queryKey: ['branding', 'platform-domain'] });
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Could not save'),
  });

  const d = query.data;
  if (!d) return null;
  const shown = value ?? d.baseDomain;

  return (
    <Card title="Platform subdomains">
      <div className="space-y-3 p-5">
        <p className="text-sm text-muted">
          One base domain gives every organization its own address automatically —{' '}
          <code className="rounded border border-line bg-surface-2 px-1.5 py-0.5 font-mono text-xs">
            &lt;org-slug&gt;.{d.baseDomain || d.platformHost}
          </code>{' '}
          — with that organization's branding on the login page. A custom domain set above
          always wins over the subdomain.
        </p>
        <div className="flex max-w-md flex-wrap items-end gap-2">
          <Field label="Base domain">
            <Input
              value={shown}
              onChange={(e) => setValue(e.target.value)}
              placeholder={d.platformHost}
              className="font-mono"
            />
          </Field>
          <Button
            variant="primary"
            loading={save.isPending}
            onClick={() => save.mutate(shown.trim().toLowerCase())}
          >
            {d.baseDomain ? 'Update' : 'Enable'}
          </Button>
          {d.baseDomain && (
            <Button variant="ghost" loading={save.isPending} onClick={() => save.mutate('')}>
              Switch off
            </Button>
          )}
        </div>
        {d.baseDomain && d.example && (
          <p className="text-sm text-muted">
            This organization answers at{' '}
            <code className="rounded border border-line bg-surface-2 px-1.5 py-0.5 font-mono text-xs">
              {d.example}
            </code>
          </p>
        )}
        {error && <p className="text-sm font-medium text-danger">{error}</p>}
        {notice && <p className="text-sm text-success">{notice}</p>}
        <div className="rounded-lg border border-line bg-surface-2 px-3 py-2 text-xs text-muted">
          <p className="mb-1 font-medium text-ink">Your two DNS-side steps:</p>
          <p>
            1. A wildcard record — <code className="font-mono">*.{d.baseDomain || 'base'}</code> —
            pointing at this server (A/AAAA or CNAME to {d.platformHost}).
          </p>
          <p>2. {d.tlsNote}</p>
        </div>
      </div>
    </Card>
  );
}

function BrandForm({
  title,
  hint,
  value,
  fallback,
  endpoint,
  disabled,
  disabledReason,
}: {
  title: string;
  hint: string;
  value: Branding;
  fallback: Branding;
  endpoint: string;
  disabled?: boolean;
  disabledReason?: string;
}) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<Branding>(value);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  // A save returns the stored row; adopting it keeps the form honest about
  // what was actually kept rather than what was typed.
  useEffect(() => setForm(value), [value]);

  const set = (patch: Partial<Branding>) => {
    setForm((f) => ({ ...f, ...patch }));
    setSaved(false);
  };

  const save = useMutation({
    mutationFn: () => api.put<Branding>(endpoint, form),
    onSuccess: async () => {
      setError(null);
      setSaved(true);
      await queryClient.invalidateQueries({ queryKey: ['branding'] });
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Could not save'),
  });

  const pickLogo = (file: File) => {
    setSaved(false);
    if (!LOGO_TYPES.includes(file.type)) {
      setError('The logo must be a PNG, JPEG, WebP or GIF.');
      return;
    }
    if (file.size > LOGO_MAX_BYTES) {
      setError(`That image is ${Math.round(file.size / 1024)} KB — use one under 256 KB.`);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setError(null);
      set({ logoDataUri: String(reader.result) });
    };
    reader.onerror = () => setError('Could not read that file.');
    reader.readAsDataURL(file);
  };

  const shownLogo = form.logoDataUri || fallback.logoDataUri || DEFAULT_BRAND.logo;

  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <Image className="size-4 text-muted" />
          {title}
          {saved && (
            <Pill tone="success" dot>
              saved
            </Pill>
          )}
        </span>
      }
    >
      <p className="border-b border-line px-5 py-2 text-xs text-faint">{hint}</p>

      {disabled ? (
        <p className="p-5 text-sm text-muted">{disabledReason}</p>
      ) : (
        <div className="space-y-5 p-5">
          <div className="flex flex-wrap items-start gap-5">
            <div className="flex flex-col items-center gap-2">
              <div className="flex size-20 items-center justify-center rounded-xl border border-line bg-surface-2 p-2">
                <img src={shownLogo} alt="" className="max-h-full max-w-full object-contain" />
              </div>
              <div className="flex gap-1">
                <Button size="sm" icon={<Upload className="size-3.5" />} onClick={() => fileInput.current?.click()}>
                  Logo
                </Button>
                {form.logoDataUri && (
                  <Button
                    size="sm"
                    variant="ghost"
                    icon={<X className="size-3.5" />}
                    onClick={() => set({ logoDataUri: null })}
                    title="Fall back to the logo behind this one"
                  >
                    Clear
                  </Button>
                )}
              </div>
              <input
                ref={fileInput}
                type="file"
                accept={LOGO_TYPES.join(',')}
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) pickLogo(file);
                  e.target.value = '';
                }}
              />
            </div>

            <div className="grid min-w-[16rem] flex-1 gap-4 sm:grid-cols-2">
              <Field label="Name" hint={`Blank shows "${fallback.displayName || DEFAULT_BRAND.displayName}"`}>
                <Input
                  value={form.displayName ?? ''}
                  onChange={(e) => set({ displayName: e.target.value })}
                  placeholder={fallback.displayName || DEFAULT_BRAND.displayName}
                />
              </Field>
              <Field label="Tagline" hint="The line under the name">
                <Input
                  value={form.tagline ?? ''}
                  onChange={(e) => set({ tagline: e.target.value })}
                  placeholder={fallback.tagline || DEFAULT_BRAND.tagline}
                />
              </Field>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <Field label="Accent colour" hint="Buttons, links and the active nav item">
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  className="h-9 w-12 cursor-pointer rounded border border-line bg-surface"
                  value={form.accentColor ?? fallback.accentColor ?? '#2563eb'}
                  onChange={(e) => set({ accentColor: e.target.value })}
                  aria-label="Accent colour"
                />
                <Input
                  value={form.accentColor ?? ''}
                  onChange={(e) => set({ accentColor: e.target.value })}
                  placeholder={fallback.accentColor ?? '#2563eb'}
                  className="font-mono"
                />
                {form.accentColor && (
                  <Button size="sm" variant="ghost" onClick={() => set({ accentColor: null })}>
                    Clear
                  </Button>
                )}
              </div>
            </Field>
            <Field label="Support email" hint="Linked in the footer">
              <Input
                type="email"
                value={form.supportEmail ?? ''}
                onChange={(e) => set({ supportEmail: e.target.value })}
                placeholder="support@example.com"
              />
            </Field>
            <Field label="Support link" hint="A help desk or portal">
              <Input
                value={form.supportUrl ?? ''}
                onChange={(e) => set({ supportUrl: e.target.value })}
                placeholder="https://help.example.com"
              />
            </Field>
            <Field label="Footer line" hint="Replaces the product attribution">
              <Input
                value={form.footerNote ?? ''}
                onChange={(e) => set({ footerNote: e.target.value })}
                placeholder="© Example Networks"
              />
            </Field>
          </div>

          {error && <ErrorNotice error={new Error(error)} />}

          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setForm(value)} disabled={save.isPending}>
              Reset
            </Button>
            <Button variant="primary" loading={save.isPending} onClick={() => save.mutate()}>
              Save
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

/**
 * Domains are stated, not offered.
 *
 * A subdomain per organization and a custom domain by CNAME both need an nginx
 * vhost and a certificate issued for that name, neither of which the app can
 * do from a form. A field that saved a domain and then served nothing would
 * look exactly like a working one, so this says what is required instead.
 */
/**
 * The org's own address, done in the order the operator set: verify the CNAME
 * FIRST, and only then let the platform write the nginx host and try for TLS.
 * The write goes through the simpleacs-vhost root helper; on a dev machine
 * the dry-run adapter answers so the flow is walkable end to end.
 *
 * A domain may have a twin: `smartolt.<domain>`, serving only the SmartOLT-
 * compatible API at SmartOLT's own path form (the operator's ask, 2026-09-14:
 * smartolt.acs.mymangaconnect.ke → …/api). It is OPTIONAL and AUTOMATIC
 * (operator, 2026-09-14: "make the smartolt domain optional in the cname
 * settings"; "make it automatic when the cname is live"): a switch on the
 * live box (and on the pre-apply form while no domain is applied, default
 * off) says whether the org wants it, and the platform writes it the moment
 * its CNAME resolves — the operator's only step is the record. Once a domain
 * is live the form sends no `smartolt` at all (`applyBody`): a re-apply is
 * the TLS retry and a changed domain inherits the org's wish, and the live
 * box's switch is the one control — a form that always sent its own switch
 * turned a live twin off in the row, unremoved (review, 2026-09-14). So the
 * live box is a ROW PER HOST
 * ("show which cnames are live"): the hostname, what DNS says, whether the
 * block is enabled, TLS until when, and the one sentence to act on when
 * something is missing. The card polls the status every thirty seconds while
 * any row is not fully live and stops when everything is green
 * (`lib/domain-hosts.ts` decides both the words and the cadence).
 */
function DomainCard({ hasOrg }: { hasOrg: boolean }) {
  const queryClient = useQueryClient();
  const [domain, setDomain] = useState('');
  const [verdict, setVerdict] = useState<null | {
    ok: boolean;
    expected: string;
    found: string[];
    detail: string;
  }>(null);
  /** The pre-apply form's switch: include the twin in a first apply. Off by default. */
  const [withSmartolt, setWithSmartolt] = useState(false);
  const [notes, setNotes] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  const current = useQuery({
    queryKey: ['branding-domain'],
    queryFn: () => api.get<DomainStatusView>('/branding/domain'),
    enabled: hasOrg,
    // Thirty seconds while a CNAME, a block or a certificate is still to
    // land; off once every row is green — the watcher finishes hosts within
    // five minutes of DNS answering, and the rows should show it happening.
    refetchInterval: (query) => domainPollInterval(query.state.data),
  });

  const verify = useMutation({
    mutationFn: () => api.post<NonNullable<typeof verdict>>('/branding/domain/verify', { domain: domain.trim() }),
    onSuccess: (v) => {
      setVerdict(v);
      setError(null);
    },
    onError: (err) => {
      setVerdict(null);
      setError(err instanceof Error ? err.message : 'Could not check the domain');
    },
  });

  const apply = useMutation({
    mutationFn: () =>
      api.post<{ domain: string; notes: string[]; smartoltEnabled: boolean }>(
        '/branding/domain/apply',
        applyBody(current.data, domain.trim(), withSmartolt),
      ),
    onSuccess: async (r) => {
      setNotes(r.notes);
      setVerdict(null);
      setDomain('');
      setWithSmartolt(false);
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['branding-domain'] });
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Could not create the host'),
  });

  const remove = useMutation({
    mutationFn: () => api.delete('/branding/domain'),
    onSuccess: async () => {
      setNotes([]);
      await queryClient.invalidateQueries({ queryKey: ['branding-domain'] });
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Could not remove the domain'),
  });

  // The twin's switch on the live box. Nothing blocks: the request goes, the
  // helper's lines land in the notes, and the rows re-read.
  const smartolt = useMutation({
    mutationFn: (enabled: boolean) =>
      api.put<{ enabled: boolean; domain: string | null; notes: string[] }>('/branding/domain/smartolt', { enabled }),
    onSuccess: async (r) => {
      setNotes(r.notes);
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['branding-domain'] });
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Could not change the SmartOLT-compatible host'),
  });

  const target = current.data?.target ?? 'this platform';
  const rows = hostRows(current.data);

  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <Globe className="size-4 text-muted" />
          Custom domain
        </span>
      }
    >
      {!hasOrg ? (
        <p className="p-5 text-sm text-muted">Select an organization in the top bar first.</p>
      ) : (
        <div className="space-y-4 p-5">
          {error && <ErrorNotice error={error} />}

          {current.data?.domain ? (
            <div
              className={`space-y-3 rounded-lg border px-4 py-3 ${
                rows.every((r) => r.live) ? 'border-success/40 bg-success-soft' : 'border-line bg-surface-2'
              }`}
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <span className="flex items-center gap-2 text-sm">
                  <CheckCircle2 className={`size-4 ${rows.every((r) => r.live) ? 'text-success' : 'text-muted'}`} />
                  <span className="font-mono text-ink">{current.data.domain}</span>
                  <span className="text-muted">
                    {rows.every((r) => r.live) ? 'is live for this organization.' : 'is being finished for this organization.'}
                  </span>
                </span>
                <Button size="sm" variant="danger" loading={remove.isPending} onClick={() => remove.mutate()}>
                  Remove
                </Button>
              </div>

              {/* One row per host: what DNS says, whether the block is up,
                  TLS until when, and the one thing to do when something is
                  missing. The twin's row appears only while its switch is on. */}
              <div className="space-y-2">
                {rows.map((row) => (
                  <HostStatusRow key={row.host} row={row} />
                ))}
              </div>

              {current.data.smartoltHost && (
                <label className="flex cursor-pointer items-start gap-2 text-sm text-ink">
                  <input
                    type="checkbox"
                    className="mt-0.5 size-4 accent-primary"
                    checked={current.data.smartoltEnabled}
                    disabled={smartolt.isPending}
                    onChange={(e) => smartolt.mutate(e.target.checked)}
                  />
                  <span>
                    SmartOLT-compatible endpoint on{' '}
                    <span className="font-mono">{current.data.smartoltHost}</span>
                    <span className="block text-xs text-muted">
                      Optional. For scripts that still carry SmartOLT’s hostname: they use{' '}
                      <span className="font-mono text-ink">https://{current.data.smartoltHost}/api/…</span>.
                      {current.data.smartoltEnabled ? (
                        <>
                          {' '}
                          CNAME <span className="font-mono text-ink">{current.data.smartoltHost}</span> to{' '}
                          <span className="font-mono text-ink">{target}</span>; the host is created automatically
                          when the CNAME is live. Switching it off removes the host and its certificate.
                        </>
                      ) : (
                        <> Switch it on and CNAME that name to <span className="font-mono text-ink">{target}</span>; the
                        platform creates the host automatically when the CNAME is live.</>
                      )}
                    </span>
                  </span>
                </label>
              )}

              <p className="text-xs text-muted">
                Creating the host for a different domain replaces this one — its hosts and certificates
                are removed with the change; the switch above carries over. Creating it again for the
                same domain retries TLS and changes nothing else.
              </p>
            </div>
          ) : (
            <p className="text-sm text-muted">
              Give this organization its own address. At your DNS host, create a CNAME from the
              domain to <span className="font-mono text-ink">{target}</span>, verify it below, and
              the platform writes the web-server host and requests TLS itself. Optionally it also
              serves <span className="font-mono text-ink">smartolt.</span>
              <span className="text-faint">‹domain›</span> for scripts that still carry SmartOLT’s
              hostname — tick the switch below and CNAME that name to{' '}
              <span className="font-mono text-ink">{target}</span> too; it is created automatically
              once its CNAME is live.
            </p>
          )}

          <div className="max-w-md space-y-3">
            <Field label="Domain" hint={`CNAME it to ${target} first`}>
              <Input
                value={domain}
                placeholder="portal.yourisp.com"
                className="font-mono"
                onChange={(e) => {
                  setDomain(e.target.value);
                  setVerdict(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && domain.trim()) verify.mutate();
                }}
              />
            </Field>
            {/* The same switch as the live box, for a FIRST apply only: off
                by default (operator, 2026-09-14: optional). The twin is
                written with the apply only when its CNAME already resolves;
                else the platform writes it when DNS lands. With a domain
                live the switch above is the org's wish and this form sends
                none — so it shows none either. */}
            {formOffersSmartolt(current.data) && (
              <label className="flex cursor-pointer items-start gap-2 text-sm text-ink">
                <input
                  type="checkbox"
                  className="mt-0.5 size-4 accent-primary"
                  checked={withSmartolt}
                  disabled={apply.isPending}
                  onChange={(e) => setWithSmartolt(e.target.checked)}
                />
                <span>
                  SmartOLT-compatible endpoint on <span className="font-mono">smartolt.</span>
                  <span className="font-mono text-faint">{domain.trim() ? domain.trim().toLowerCase() : '‹domain›'}</span>
                  <span className="block text-xs text-muted">
                    Optional. CNAME that name to <span className="font-mono text-ink">{target}</span> as well; it is
                    created automatically when the CNAME is live.
                  </span>
                </span>
              </label>
            )}
            {/* Under the field, not beside it — the row crowded the input off
                its line (operator's call). */}
            <div className="flex flex-wrap gap-2">
              <Button variant="secondary" disabled={!domain.trim()} loading={verify.isPending} onClick={() => verify.mutate()}>
                Verify the CNAME
              </Button>
              <Button
                variant="primary"
                disabled={!verdict?.ok}
                loading={apply.isPending}
                title={verdict?.ok ? undefined : 'Verify the CNAME first — the host is only written once DNS points here.'}
                onClick={() => apply.mutate()}
              >
                Create the host
              </Button>
            </div>
          </div>

          {verdict && (
            <p
              className={`rounded-lg border px-3 py-2 text-sm ${
                verdict.ok
                  ? 'border-success/40 bg-success-soft text-success'
                  : 'border-warn/40 bg-warn-soft text-warn'
              }`}
            >
              {verdict.detail}
            </p>
          )}

          {notes.length > 0 && (
            <div className="rounded-lg border border-line bg-surface-2 p-3 font-mono text-xs text-muted">
              {notes.map((n) => (
                <div key={n}>{n}</div>
              ))}
            </div>
          )}

          <p className="flex items-start gap-2 text-[11px] text-faint">
            <Info className="mt-0.5 size-3.5 shrink-0" />
            The host is only ever written after the CNAME verifies. TLS is requested immediately;
            if DNS was still settling, the site serves HTTP and the platform retries the
            certificate within five minutes of DNS answering — re-creating the host retries at once.
          </p>
        </div>
      )}
    </Card>
  );
}

const TONE: Record<RowTone, string> = {
  ok: 'text-success',
  warn: 'text-warn',
  muted: 'text-muted',
};

/**
 * One host of the domain: hostname · CNAME · host · TLS, and under them the
 * one sentence to act on while the row is not green. The words come from
 * `lib/domain-hosts.ts`; this only lays them out.
 */
function HostStatusRow({ row }: { row: HostRow }) {
  return (
    <div className="rounded-md border border-line/60 bg-surface px-3 py-2 text-xs">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        <span className="font-mono text-ink">{row.host}</span>
        <span className="text-faint">
          CNAME <span className={TONE[row.cname.tone]}>{row.cname.label}</span>
        </span>
        <span className="text-faint">
          host <span className={TONE[row.vhost.tone]}>{row.vhost.label}</span>
        </span>
        <span className="text-faint">
          TLS <span className={TONE[row.tls.tone]}>{row.tls.label}</span>
        </span>
        {row.role === 'smartolt' && <Pill tone="neutral">SmartOLT-compatible</Pill>}
      </div>
      {row.next && <p className="mt-1 text-muted">{row.next}</p>}
    </div>
  );
}
